import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Shield, Check, Zap, Clock, Bell, Cloud, FileText, ArrowRight, Loader2,
  Server, Users, Globe, Download, BarChart3, Lock, Activity, ShoppingCart,
  Search, TrendingUp, Mail, X, Tag
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { api } from '@/lib/api';

function getDeviceId() {
  let id = localStorage.getItem('ac_device_id');
  if (!id) {
    id = 'dev_' + Math.random().toString(36).slice(2, 14) + Date.now().toString(36);
    localStorage.setItem('ac_device_id', id);
  }
  return id;
}

// All plans with monthly + annual variants
const PLANS = {
  pro: {
    name: 'Pro', monthly: { id: 'pro_monthly', price: 9 }, annual: { id: 'pro_yearly', price: 79, monthlyEquiv: 6.58, savings: 29 },
  },
  teams: {
    name: 'Teams', monthly: { id: 'teams_monthly', price: 49 }, annual: { id: 'teams_yearly', price: 490, monthlyEquiv: 40.83, savings: 98 },
  },
  teams_verify: {
    name: 'Teams + Verify', monthly: { id: 'teams_verify_monthly', price: 199 }, annual: { id: 'teams_verify_yearly', price: 1990, monthlyEquiv: 165.83, savings: 398 },
  },
  verify_merchant: {
    name: 'Verify Merchant', monthly: { id: 'verify_merchant_monthly', price: 79 }, annual: { id: 'verify_merchant_yearly', price: 790, monthlyEquiv: 65.83, savings: 158 },
  },
  verify_plus: {
    name: 'Verify Plus', monthly: { id: 'verify_plus_monthly', price: 199 }, annual: { id: 'verify_plus_yearly', price: 1990, monthlyEquiv: 165.83, savings: 398 },
  },
  verify_ops: {
    name: 'Commerce Ops', monthly: { id: 'verify_ops_monthly', price: 499 }, annual: { id: 'verify_ops_yearly', price: 4990, monthlyEquiv: 415.83, savings: 998 },
  },
};

export default function PricingPage() {
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [entitlements, setEntitlements] = useState(null);
  const [tab, setTab] = useState('individual');
  const [billing, setBilling] = useState('annual');
  const [couponCode, setCouponCode] = useState('');
  const [couponApplied, setCouponApplied] = useState(null); // { valid, percent_off, message }
  const [couponLoading, setCouponLoading] = useState(false);
  const [showExitOffer, setShowExitOffer] = useState(false);
  const [exitOfferShown, setExitOfferShown] = useState(false); // monthly | annual

  useEffect(() => { api.getEntitlements(getDeviceId()).then(setEntitlements).catch(() => {}); }, []);

  // Exit-intent: detect mouse leaving viewport or tab blur
  useEffect(() => {
    const handleMouseLeave = (e) => {
      if (e.clientY <= 0 && !exitOfferShown && !couponApplied?.valid) {
        setShowExitOffer(true);
        setExitOfferShown(true);
      }
    };
    const handleVisibilityChange = () => {
      if (document.hidden && !exitOfferShown && !couponApplied?.valid) {
        setShowExitOffer(true);
        setExitOfferShown(true);
      }
    };
    document.addEventListener('mouseleave', handleMouseLeave);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('mouseleave', handleMouseLeave);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [exitOfferShown, couponApplied]);

  const validateCoupon = async (code) => {
    if (!code.trim()) return;
    setCouponLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/v1/billing/validate-coupon`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: code.trim() }),
      });
      const data = await res.json();
      setCouponApplied(data);
      if (data.valid) {
        toast.success(data.message);
        setBilling('annual');
      } else {
        toast.error(data.message);
      }
    } catch { toast.error('Could not validate coupon'); }
    finally { setCouponLoading(false); }
  };

  const handleCheckout = async (planId) => {
    setLoadingPlan(planId);
    try {
      const body = { plan_id: planId, origin_url: window.location.origin, device_id: getDeviceId() };
      if (couponApplied?.valid && couponApplied?.code) {
        body.coupon_code = couponApplied.code;
      }
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/v1/billing/checkout`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Checkout failed');
      const data = await res.json();
      if (data.url) window.location.href = data.url;
    } catch (err) { toast.error(err.message); setLoadingPlan(null); }
  };

  const isPro = entitlements?.plan === 'pro' && (entitlements?.status === 'active' || entitlements?.status === 'past_due');
  const isAnnual = billing === 'annual';

  function getPlanId(planKey) {
    return isAnnual ? PLANS[planKey].annual.id : PLANS[planKey].monthly.id;
  }

  function PriceDisplay({ planKey, perUnit = '' }) {
    const plan = PLANS[planKey];
    if (isAnnual) {
      return (
        <div className="mb-3">
          <div className="flex items-baseline gap-2">
            <span className="text-3xl font-bold text-slate-900">${plan.annual.monthlyEquiv.toFixed(0)}</span>
            <span className="text-slate-500 text-sm">/mo{perUnit}</span>
          </div>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-xs text-slate-400 line-through">${plan.monthly.price * 12}/yr</span>
            <span className="text-xs font-semibold text-emerald-600">${plan.annual.price}/yr</span>
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 font-semibold">Save ${plan.annual.savings}</span>
          </div>
        </div>
      );
    }
    return (
      <div className="mb-3">
        <span className="text-3xl font-bold text-slate-900">${plan.monthly.price}</span>
        <span className="text-slate-500 text-sm ml-1">/mo{perUnit}</span>
      </div>
    );
  }

  function CheckoutBtn({ planKey, label, variant = 'default', className = '' }) {
    const pid = getPlanId(planKey);
    const isLoading = loadingPlan === pid;
    const btnClass = variant === 'outline'
      ? 'w-full rounded-full gap-2'
      : `w-full rounded-full gap-2 ${className || 'bg-[#007AFF] hover:bg-[#0062CC]'}`;
    return (
      <Button variant={variant === 'outline' ? 'outline' : 'default'} className={btnClass}
        disabled={!!loadingPlan || (planKey === 'pro' && isPro)}
        onClick={() => handleCheckout(pid)}
        data-testid={`cta-${planKey}`}>
        {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : (planKey === 'pro' && isPro) ? 'Already Pro' : <><ArrowRight className="w-4 h-4" />{label}</>}
      </Button>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="pricing-page">
      <header className="border-b border-slate-100 sticky top-0 z-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#007AFF] flex items-center justify-center"><Shield className="w-5 h-5 text-white" strokeWidth={2} /></div>
            <span className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>AgentCheck</span>
          </Link>
          <nav className="flex items-center gap-6">
            <Link to="/" className="text-sm text-slate-600 hover:text-slate-900">Scan</Link>
            <Link to="/leaderboard/ai-bots" className="text-sm text-slate-600 hover:text-slate-900">Leaderboard</Link>
            <Link to="/verify" className="text-sm text-slate-600 hover:text-slate-900">Verify</Link>
          </nav>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
        <div className="text-center mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Manrope' }}>Simple pricing. Powerful tools.</h1>
          <p className="text-base text-slate-500 max-w-xl mx-auto">Free for individuals. Pro for power users. Verify for merchants and teams who need server-log proof.</p>
        </div>

        {isPro && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-6 text-center" data-testid="pro-active-badge">
            <div className="flex items-center justify-center gap-2"><Check className="w-5 h-5 text-emerald-600" /><span className="text-sm font-semibold text-emerald-700">You're on Pro</span></div>
            {entitlements?.has_billing && <button data-testid="manage-billing-btn" onClick={async () => { try { const r = await api.createBillingPortal(getDeviceId()); if (r.portal_url) window.location.href = r.portal_url; } catch { toast.error('Could not open billing portal'); } }} className="mt-2 text-xs text-[#007AFF] hover:text-[#0062CC] font-medium underline underline-offset-2">Manage billing</button>}
          </div>
        )}

        {/* Billing toggle */}
        <div className="flex justify-center mb-6">
          <div className="inline-flex bg-slate-100 rounded-full p-1" data-testid="billing-toggle">
            <button onClick={() => setBilling('monthly')} data-testid="billing-monthly"
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${billing === 'monthly' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`}>
              Monthly
            </button>
            <button onClick={() => setBilling('annual')} data-testid="billing-annual"
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-1.5 ${billing === 'annual' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`}>
              Annual <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-semibold">2 months free</span>
            </button>
          </div>
        </div>

        {/* Coupon code input */}
        <div className="flex justify-center mb-6">
          <div className="flex items-center gap-2" data-testid="coupon-section">
            <input type="text" value={couponCode} onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
              placeholder="Coupon code" disabled={couponLoading || couponApplied?.valid}
              data-testid="coupon-input"
              className={`h-9 px-4 rounded-full border text-sm w-40 ${couponApplied?.valid ? 'border-emerald-300 bg-emerald-50 text-emerald-700' : 'border-slate-200'}`}
              onKeyDown={(e) => e.key === 'Enter' && validateCoupon(couponCode)}
            />
            {couponApplied?.valid ? (
              <span className="flex items-center gap-1 text-xs font-semibold text-emerald-600" data-testid="coupon-applied">
                <Check className="w-3.5 h-3.5" />{couponApplied.percent_off}% off applied
              </span>
            ) : (
              <Button variant="outline" size="sm" className="h-9 rounded-full text-xs" disabled={couponLoading || !couponCode.trim()}
                onClick={() => validateCoupon(couponCode)} data-testid="coupon-apply-btn">
                {couponLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Apply'}
              </Button>
            )}
          </div>
        </div>

        {/* Tab switcher */}
        <div className="flex justify-center gap-2 mb-8">
          {[
            { key: 'individual', label: 'Individuals', icon: Shield },
            { key: 'teams', label: 'Teams', icon: Users },
            { key: 'ecommerce', label: 'E-commerce Verify', icon: ShoppingCart },
          ].map(({ key, label, icon: Icon }) => (
            <button key={key} onClick={() => setTab(key)} data-testid={`tab-${key}`}
              className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all flex items-center gap-1.5 ${tab === key ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'}`}>
              <Icon className="w-3.5 h-3.5" />{label}
            </button>
          ))}
        </div>

        {/* === INDIVIDUALS === */}
        {tab === 'individual' && (
          <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto mb-12" data-testid="individual-plans">
            <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="free-plan">
              <h3 className="text-lg font-semibold text-slate-900 mb-1" style={{ fontFamily: 'Manrope' }}>Free</h3>
              <div className="mb-3"><span className="text-3xl font-bold text-slate-900">$0</span><span className="text-slate-500 text-sm ml-1">forever</span></div>
              <ul className="space-y-2 mb-5">
                {['Site Scan reports + share links', 'Monitor Today view', 'Timeline up to 7 days', 'Shut / Pause / Continue rules', 'Export & delete local data'].map(f =>
                  <li key={f} className="flex items-start gap-2 text-sm text-slate-600"><Check className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />{f}</li>
                )}
              </ul>
              <Button variant="outline" className="w-full rounded-full" disabled>Current plan</Button>
            </div>
            <div className={`bg-white rounded-2xl p-6 relative ${isAnnual ? 'border-2 border-[#007AFF] shadow-lg shadow-blue-50' : 'border border-slate-200'}`} data-testid="pro-plan">
              {isAnnual && <div className="absolute -top-3 left-1/2 -translate-x-1/2"><span className="px-3 py-1 rounded-full bg-[#007AFF] text-white text-xs font-semibold">2 months free</span></div>}
              <h3 className="text-lg font-semibold text-slate-900 mb-1" style={{ fontFamily: 'Manrope' }}>Pro</h3>
              <PriceDisplay planKey="pro" />
              <p className="text-xs text-slate-500 mb-3">Everything in Free, plus:</p>
              <ul className="space-y-2 mb-5">
                {[{ t: '90-day + YTD timeline history', i: Clock }, { t: 'Doorbell alerts (new agent, spike)', i: Bell }, { t: 'Cloud Sync (privacy-safe rollups)', i: Cloud }, { t: 'Auto-policies (pause ads, block replay)', i: FileText }, { t: 'Priority support + early features', i: Zap }].map(({ t, i: I }) =>
                  <li key={t} className="flex items-start gap-2 text-sm text-slate-700"><I className="w-4 h-4 text-[#007AFF] flex-shrink-0 mt-0.5" />{t}</li>
                )}
              </ul>
              <CheckoutBtn planKey="pro" label={`Get Pro ${isAnnual ? 'Yearly' : 'Monthly'}`} />
            </div>
          </div>
        )}

        {/* === TEAMS === */}
        {tab === 'teams' && (
          <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto mb-12" data-testid="teams-plans">
            <div className="bg-white rounded-2xl border border-slate-200 p-6 relative" data-testid="teams-plan">
              <h3 className="text-lg font-semibold text-slate-900 mb-1" style={{ fontFamily: 'Manrope' }}>Teams</h3>
              <PriceDisplay planKey="teams" />
              <p className="text-xs text-slate-500 mb-3">Best for: small orgs & agencies</p>
              <ul className="space-y-2 mb-5">
                {[{ t: '5 seats (extension Pro features)', i: Users }, { t: 'Shared policies + doorbell settings', i: Lock }, { t: '90-day history per seat', i: Clock }, { t: 'Org admin controls', i: Shield }].map(({ t, i: I }) =>
                  <li key={t} className="flex items-start gap-2 text-sm text-slate-700"><I className="w-4 h-4 text-[#007AFF] flex-shrink-0 mt-0.5" />{t}</li>
                )}
              </ul>
              <CheckoutBtn planKey="teams" label="Get Teams" variant="outline" />
            </div>
            <div className={`bg-white rounded-2xl p-6 relative ${isAnnual ? 'border-2 border-[#007AFF] shadow-lg shadow-blue-50' : 'border border-slate-200'}`} data-testid="teams-verify-plan">
              {isAnnual && <div className="absolute -top-3 left-1/2 -translate-x-1/2"><span className="px-3 py-1 rounded-full bg-[#007AFF] text-white text-xs font-semibold">Best value</span></div>}
              <h3 className="text-lg font-semibold text-slate-900 mb-1" style={{ fontFamily: 'Manrope' }}>Teams + Verify</h3>
              <PriceDisplay planKey="teams_verify" />
              <p className="text-xs text-slate-500 mb-3">Best for: e-commerce teams</p>
              <ul className="space-y-2 mb-5">
                {[{ t: '5 seats + shared policies', i: Users }, { t: '1 verified domain + 50 GB/mo', i: Server }, { t: '90-day retention', i: Clock }, { t: 'Weekly email digest', i: Mail }, { t: 'Verified Activity share card', i: FileText }].map(({ t, i: I }) =>
                  <li key={t} className="flex items-start gap-2 text-sm text-slate-700"><I className="w-4 h-4 text-[#007AFF] flex-shrink-0 mt-0.5" />{t}</li>
                )}
              </ul>
              <CheckoutBtn planKey="teams_verify" label="Get Teams + Verify" />
            </div>
          </div>
        )}

        {/* === E-COMMERCE VERIFY === */}
        {tab === 'ecommerce' && (
          <>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-8 text-center" data-testid="ecommerce-callout">
              <p className="text-sm text-amber-800"><ShoppingCart className="w-4 h-4 inline mr-1" /><span className="font-semibold">Bot Activity & Scraping Intelligence from your server logs.</span> Who scrapes your catalog? Which endpoints are hit? Is it growing?</p>
            </div>
            <div className="grid md:grid-cols-3 gap-6 mb-12" data-testid="ecommerce-plans">
              <div className="bg-white rounded-2xl border border-slate-200 p-6 relative" data-testid="verify-merchant">
                <div className="absolute -top-3 left-1/2 -translate-x-1/2"><span className="px-3 py-1 rounded-full bg-amber-600 text-white text-xs font-semibold">E-commerce</span></div>
                <h3 className="text-lg font-semibold text-slate-900 mb-1" style={{ fontFamily: 'Manrope' }}>Verify Merchant</h3>
                <PriceDisplay planKey="verify_merchant" perUnit=" per store" />
                <p className="text-xs text-slate-500 mb-3">Best for: single Shopify brand</p>
                <ul className="space-y-2 mb-5">
                  {[{ t: '1 store/domain', i: Globe }, { t: 'Up to 50 GB logs/month', i: Server }, { t: 'Bot classification (UA + ASN + behavior)', i: Activity }, { t: 'Catalog scraping detection', i: Search }, { t: 'Timeseries + sessions + top endpoints', i: BarChart3 }, { t: 'Weekly email digest', i: Mail }, { t: '30-day retention', i: Clock }].map(({ t, i: I }) =>
                    <li key={t} className="flex items-start gap-2 text-sm text-slate-700"><I className="w-4 h-4 text-[#007AFF] flex-shrink-0 mt-0.5" />{t}</li>
                  )}
                </ul>
                <CheckoutBtn planKey="verify_merchant" label="Start Verify" className="bg-amber-600 hover:bg-amber-700" />
              </div>
              <div className={`bg-white rounded-2xl p-6 relative ${isAnnual ? 'border-2 border-[#007AFF] shadow-lg shadow-blue-50' : 'border border-slate-200'}`} data-testid="verify-plus">
                {isAnnual && <div className="absolute -top-3 left-1/2 -translate-x-1/2"><span className="px-3 py-1 rounded-full bg-[#007AFF] text-white text-xs font-semibold">Growing brands</span></div>}
                <h3 className="text-lg font-semibold text-slate-900 mb-1" style={{ fontFamily: 'Manrope' }}>Verify Plus</h3>
                <PriceDisplay planKey="verify_plus" />
                <p className="text-xs text-slate-500 mb-3">Best for: multi-storefront brands</p>
                <ul className="space-y-2 mb-5">
                  {[{ t: '3 domains', i: Globe }, { t: '200 GB logs/month', i: Server }, { t: '90-day retention', i: Clock }, { t: 'Weekly reprocessing', i: TrendingUp }, { t: 'Email alerts (spikes + new bots)', i: Bell }, { t: 'Priority support', i: Zap }].map(({ t, i: I }) =>
                    <li key={t} className="flex items-start gap-2 text-sm text-slate-700"><I className="w-4 h-4 text-[#007AFF] flex-shrink-0 mt-0.5" />{t}</li>
                  )}
                </ul>
                <CheckoutBtn planKey="verify_plus" label="Get Verify Plus" />
              </div>
              <div className="bg-white rounded-2xl border border-slate-200 p-6 relative" data-testid="verify-ops">
                <div className="absolute -top-3 left-1/2 -translate-x-1/2"><span className="px-3 py-1 rounded-full bg-purple-600 text-white text-xs font-semibold">Enterprise</span></div>
                <h3 className="text-lg font-semibold text-slate-900 mb-1" style={{ fontFamily: 'Manrope' }}>Commerce Ops</h3>
                <PriceDisplay planKey="verify_ops" />
                <p className="text-xs text-slate-500 mb-3">Best for: agencies + large merchants</p>
                <ul className="space-y-2 mb-5">
                  {[{ t: '10 domains', i: Globe }, { t: '500 GB logs/month', i: Server }, { t: '180-day retention', i: Clock }, { t: 'S3 direct uploads', i: Cloud }, { t: 'API access + Slack alerts', i: Download }, { t: 'Onboarding + quarterly review', i: Zap }].map(({ t, i: I }) =>
                    <li key={t} className="flex items-start gap-2 text-sm text-slate-700"><I className="w-4 h-4 text-[#007AFF] flex-shrink-0 mt-0.5" />{t}</li>
                  )}
                </ul>
                <CheckoutBtn planKey="verify_ops" label="Get Commerce Ops" className="bg-purple-600 hover:bg-purple-700" />
              </div>
            </div>
          </>
        )}

        {/* Compare Plans Matrix */}
        <div className="mb-8" data-testid="compare-matrix">
          <h3 className="text-lg font-semibold text-slate-900 text-center mb-6" style={{ fontFamily: 'Manrope' }}>Compare Plans</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-3 px-3 text-slate-500 font-medium">Feature</th>
                  <th className="py-3 px-2 text-center text-slate-700 font-semibold">Free</th>
                  <th className="py-3 px-2 text-center text-slate-700 font-semibold">Pro<br/><span className="text-xs font-normal text-slate-400">$9/mo</span></th>
                  <th className="py-3 px-2 text-center text-slate-700 font-semibold">Teams<br/><span className="text-xs font-normal text-slate-400">$49/mo</span></th>
                  <th className="py-3 px-2 text-center text-[#007AFF] font-semibold">Verify<br/><span className="text-xs font-normal text-slate-400">$79/mo</span></th>
                  <th className="py-3 px-2 text-center text-slate-700 font-semibold">Ops<br/><span className="text-xs font-normal text-slate-400">$499/mo</span></th>
                </tr>
              </thead>
              <tbody className="text-xs">
                {[
                  ['Site Scan + Reports', true, true, true, true, true],
                  ['Monitor (7d history)', true, true, true, false, true],
                  ['90-day / YTD History', false, true, true, false, true],
                  ['Doorbell Alerts', false, true, true, true, true],
                  ['Auto-policies', false, true, true, false, true],
                  ['Cloud Sync', false, true, true, false, true],
                  ['Seats', '1', '1', '5', '1', '20'],
                  ['Verified Sites', '-', '-', '-', '1', '10'],
                  ['Log Volume', '-', '-', '-', '50 GB', '500 GB'],
                  ['Retention', '7d', '90d', '90d', '30d', '180d'],
                  ['Email Alerts', false, false, false, true, true],
                  ['API Access', false, false, false, false, true],
                ].map(([feature, ...vals], i) => (
                  <tr key={i} className="border-b border-slate-100">
                    <td className="py-2.5 px-3 text-slate-700 font-medium">{feature}</td>
                    {vals.map((v, j) => (
                      <td key={j} className="py-2.5 px-2 text-center">
                        {v === true ? <Check className="w-4 h-4 text-emerald-500 mx-auto" /> :
                         v === false ? <span className="text-slate-300">-</span> :
                         <span className="text-slate-700">{v}</span>}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Posture vs Verify */}
        <div className="bg-slate-50 rounded-2xl p-6 mb-8">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div>
              <h3 className="text-sm font-semibold text-slate-900 mb-1">Posture vs Verified Activity</h3>
              <p className="text-xs text-slate-500 max-w-lg"><span className="font-medium text-slate-700">Posture</span> = declared in robots.txt (free). <span className="font-medium text-slate-700">Verified</span> = proven from server logs. Both useful. Verify is proof.</p>
            </div>
            <Link to="/verify"><Button variant="outline" size="sm" className="rounded-full text-xs gap-1.5" data-testid="verify-cta-bottom"><Server className="w-3.5 h-3.5" />Try free audit</Button></Link>
          </div>
        </div>

        <div className="text-center">
          <p className="text-sm text-slate-500">Secure payments via Stripe. Cancel anytime via <button onClick={async () => { try { const r = await api.createBillingPortal(getDeviceId()); if (r.portal_url) window.location.href = r.portal_url; } catch { toast.error('Subscribe first to manage billing'); } }} className="text-[#007AFF] hover:underline font-medium">Manage Billing</button>.</p>
          {!couponApplied?.valid && <p className="text-xs text-slate-400 mt-2">Have a coupon? Enter it above. <span className="font-medium">First 50 annual customers get 15% off with code 15OFFANNUAL.</span></p>}
        </div>
      </main>

      <footer className="py-8 border-t border-slate-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 text-center">
          <p className="text-sm text-slate-500">AgentCheck by Dispatch Global Services Inc (DBA Sagisu) <Link to="/privacy" className="text-[#007AFF] hover:underline">Privacy Policy</Link> <Link to="/refund" className="text-[#007AFF] hover:underline ml-2">Refund Policy</Link></p>
        </div>
      </footer>

      {/* Exit-intent offer modal */}
      {showExitOffer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" data-testid="exit-offer-modal">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-8 relative animate-in fade-in zoom-in-95">
            <button onClick={() => setShowExitOffer(false)} className="absolute top-4 right-4 text-slate-400 hover:text-slate-600" data-testid="exit-offer-close">
              <X className="w-5 h-5" />
            </button>
            <div className="text-center">
              <div className="w-14 h-14 rounded-2xl bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                <Tag className="w-7 h-7 text-emerald-600" />
              </div>
              <h2 className="text-xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope' }}>Wait — 15% off any annual plan</h2>
              <p className="text-sm text-slate-500 mb-5">First 50 customers only. Use code below to save on any annual subscription.</p>
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 mb-5">
                <p className="text-xs text-slate-500 mb-1.5">Your coupon code</p>
                <p className="text-2xl font-bold text-slate-900 tracking-wider" style={{ fontFamily: 'JetBrains Mono, monospace' }}>15OFFANNUAL</p>
                <p className="text-xs text-emerald-600 font-medium mt-1">15% off — limited to 50 redemptions</p>
              </div>
              <Button className="w-full rounded-full bg-[#007AFF] hover:bg-[#0062CC] gap-2" data-testid="exit-offer-apply"
                onClick={() => {
                  setCouponCode('15OFFANNUAL');
                  validateCoupon('15OFFANNUAL');
                  setShowExitOffer(false);
                  setBilling('annual');
                }}>
                <Tag className="w-4 h-4" /> Apply & See Annual Prices
              </Button>
              <button onClick={() => setShowExitOffer(false)} className="mt-3 text-xs text-slate-400 hover:text-slate-600">No thanks, I'll pay full price</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
